import { array } from '@common/util/array';
import { MetaDataVariables } from '@portal-core/project-files/models/meta-data-variables.model';
import { VariableFormatter, variableFormatter } from '@portal-core/project-files/variables/util/variable-formatter';

export type variableType = 'project' | 'system' | 'both';

export class VariableTextParser {

  /**
   * Lookups variables in the specified text.
   * @param text - Text to lookup variables within.
   * @param type - Specifies the variable type to lookup: project variables, system variables or both.
   * @returns An array of unique variable names if text contains any one. Otherwise, returns null.
   */
  static lookupVariables(text: string, type: variableType = 'both'): string[] {
    const variables: string[] = [];

    VariableTextParser.iterateVariables(text, (variableName) => {
      let includeVariable = (type === 'both') || ((type === 'system') === VariableTextParser.isSystem(variableName));
      if (includeVariable && !variables.includes(variableName))
        variables.push(variableName);
      return true;
    });

    return array(variables);
  }

  /**
   * Checks whether the specified text contains any variables.
   * @param text Text to check.
   * @param type - Specifies the variable type to check: project variables, system variables or both.
   * Returns true if the text contains at least one variable. Otherwise, returns false.
   */
  static hasVariables(text: string, type: variableType = 'both'): boolean {
    let hasVariables = false;

    VariableTextParser.iterateVariables(text, (variableName) => {
      let isSuitable = (type === 'both') || ((type === 'system') === VariableTextParser.isSystem(variableName));
      hasVariables = isSuitable;
      if (hasVariables)
        return false; // stop iterate variables if found
      return true;
    });

    return hasVariables;
  }

  /**
   * Returns true if the variable is system. Otherwise, returns false.
   */
  static isSystem(longName: string): boolean {
    if (!longName)
      return false;
    const parts = longName.split('.');
    return this.isSystemVariableSet(parts[0]);
  }

  /**
   * Returns true if the variable set is system. Otherwise, returns false.
   */
  static isSystemVariableSet(setName: string): boolean {
    if (!setName)
      return false;
    return setName === 'System' || setName === 'Heading';
  }

  /**
   * This code uses the same way to detect variables like the Flare VariableTextParser.ProcessVariableText method because it is used for output files.
   * It does not detect broken formatted variables that has three or more '[%=' in a row
   */
  private static iterateVariables(text: string, processVariable: (variableName: string) => boolean) {
    if (!text)
      return text;

    while (true) {
      let start: number = text.indexOf('[%=');
      if (start === -1) return;

      let end: number = text.indexOf('%]');
      if (end === -1) return;

      //skip over broken formatted variables
      let nextStart: number = text.indexOf('[%=', start + 3);
      if (nextStart !== -1 && nextStart < end) {
        // What does this code do? The 'nextEnd' variable has the same value and condition of the return as the 'end' variable above.
        // See Flare VariableTextParser.ProcessVariableText method
        let nextEnd: number = text.indexOf('%]');
        if (nextEnd === -1) return;
      }
      else {
        nextStart = start;
      }

      if (end > nextStart) {
        let variable: string = text.substring(nextStart + 3, end);
        if (variable) {
          const iterateNext = processVariable(variable);
          if (!iterateNext)
            return;
        }
      }

      end += 2; // len of %]
      if (text.length - (end) <= 0) return;
      text = text.substring(end);
    }
  }

  private evaluatedVariables: Dictionary<string> = {};
  private processingVariables: Dictionary<boolean> = {};
  private recursiveVariables: Dictionary<boolean> = {};

  constructor(private variablesMetaData: MetaDataVariables,
    private formatTextVariable: variableFormatter = VariableFormatter.formatTextVariable,
    private formatUndefinedVariable: variableFormatter = VariableFormatter.formatUndefinedVariable
  ) { }

  /**
   * Resolves all variables in the specified text. System variables will be resolved as undefined.
   */
  processVariableText(text: string, removeNewLinesAndTabs: boolean): string {
    return this.processVariableTextRecursively(text, removeNewLinesAndTabs, 0);
  }

  private processVariableTextRecursively(text: string, removeNewLinesAndTabs: boolean, nestingLevel: number): string {
    // Get all unique variables in text
    const uniqueVariables = VariableTextParser.lookupVariables(text);

    if (uniqueVariables)
      uniqueVariables.forEach(longName => {
        // Process the variable
        let value: string = this.processVariable(longName, nestingLevel);
        // Remove new lines and tabs if needed
        if (removeNewLinesAndTabs) {
          value = value.replace(/\t|\n|\r/g, ' ');
        }
        // Replace formatted variable name with its value
        const formattedVariable = this.formatTextVariable(longName);
        text = text.replaceAll(formattedVariable, value);
      });
    return text;
  }

  private processVariable(longName: string, nestingLevel: number): string {
    // Check whether the variable is system because the parser should handle project variables only.
    if (VariableTextParser.isSystem(longName))
      return this.formatUndefinedVariable(longName);

    let evaluated = this.evaluatedVariables[longName];
    // Check whether the variable is evaluated already. If no, try to evaluate.
    if (typeof evaluated === 'undefined') {
      try {
        // Check whether the variable has a recursive problem
        const recursionDetected = this.processingVariables[longName] || this.recursiveVariables[longName];
        if (recursionDetected) {
          throw Error('Recursive problem in variable.');
        }
        // Set the variable as processing
        this.processingVariables[longName] = true;
        // Evaluate variable definition
        let definition: string = this.convertToValue(longName);
        evaluated = this.processVariableTextRecursively(definition, false, nestingLevel + 1);
        this.evaluatedVariables[longName] = evaluated;
      }
      catch (error) {
        // Set the variable as a variable with recursive problem
        this.recursiveVariables[longName] = true;
        if (nestingLevel == 0)
          evaluated = `[[Variable with recursive problem: ${longName}]]`;
        else
          throw error;
      }
      finally {
        // Unset the variable as processing
        this.processingVariables[longName] = undefined;
      }
    }
    // Return the evaluated value
    return evaluated;
  }

  private convertToValue(longName: string) {
    const variable = this.variablesMetaData[longName];
    return variable ? variable.definition : this.formatUndefinedVariable(longName);
  }

}
